import { db, storage } from "./config"
import { collection, addDoc, getDocs, query, where, doc, updateDoc, getDoc } from "firebase/firestore"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { addNotification } from "./notifications"
import { resolveContactUid } from "./shopify"

export const createOffer = async (userId, card, quantity, originalPrice, offerPrice) => {
    try {
        // For a Shopify item this is the store's role id, which is what the
        // seller-side views query on. It is not an account, so the
        // notification below goes to the store's owner instead.
        const sellerRef = card?.seller || card?.seller_id || card?.merchant_id || ''
        const notifyUid = await resolveContactUid(card)
        const offersRef = collection(db, "offers")

        const docRef = await addDoc(offersRef, {
            count: quantity,
            sender: userId,
            receiver: sellerRef,
            cardId: card.id,
            picture: card.pictures[0],
            title: card.title,
            category: card.category,
            created_at: new Date(),
            original_price: originalPrice,
            offer_price: offerPrice,
            state: 'pending'
        })

        console.log("Document written with ID: ", docRef.id)

        // Send notification to seller about the new offer
        try {
            const notificationData = {
                type: 'offer_received',
                action: 'offer_made',
                listingId: card.id,
                listingTitle: card.title,
                recipientId: notifyUid || sellerRef,
                message: `You received a new offer of $${offerPrice.toFixed(2)} for your listing "${card.title}".`,
                timestamp: new Date(),
                isRead: false
            };
            
            await addNotification(notificationData);
            console.log('✅ Offer notification sent to seller:', notificationData.recipientId);
        } catch (notificationError) {
            console.error('❌ Error sending offer notification:', notificationError);
        }

        return docRef.id;
    } catch (e) {
        console.error("Error adding document: ", e)
        throw e;
    }
}

export const cancelOffer= async ( offer )=>{
    try{
        console.log('User cancel the offer');

        const offerRef = doc(db, "offers", offer.id)
        const snapshot = await getDoc(offerRef);
        if(!snapshot.exists() || snapshot.data().state !== "pending")
            throw Error("Can not cancel not pending offer");
        // Update the document with new data
        await updateDoc(offerRef, {
            state: "cancelled"
        })

        // Send notifications based on the state change
        try {
            // Send notification to seller that their offer was cancelled
            const sellerNotificationData = {
                type: 'offer_status',
                action: 'cancelled',
                listingId: offer.cardId,
                listingTitle: offer.title,
                recipientId: offer.receiver, // Notify the seller
                message: `Buyer has cancelled an offer of $${offer.offer_price.toFixed(2)} for "${offer.title}"!`,
                timestamp: new Date(),
                isRead: false
            };
            await addNotification(sellerNotificationData);
        } catch (notificationError) {
            console.error('❌ Error sending offer status notification:', notificationError);
        }
    }
    catch(error){
        console.log("Offer Cancel error", error);
        throw e;
    }
}

// Add notifications to existing offers (for retroactive notifications)
export const addNotificationsToExistingOffers = async () => {
    try {
        console.log('Adding notifications to existing offers...');
        
        const offersRef = collection(db, "offers");
        const querySnapshot = await getDocs(offersRef);
        
        let count = 0;
        for (const doc of querySnapshot.docs) {
            const offerData = { id: doc.id, ...doc.data() };
            
            // Skip if already has notifications
            if (offerData.notifications_sent) continue;
            
            try {
                console.log('Processing offer:', offerData);
                
                // Send notification to seller about the offer
                const sellerNotificationData = {
                    type: 'offer_received',
                    action: 'offer_made',
                    listingId: offerData.cardId,
                    listingTitle: offerData.title,
                    recipientId: offerData.receiver,
                    message: `You received a new offer of $${offerData.offer_price.toFixed(2)} for your listing "${offerData.title}".`,
                    timestamp: new Date(),
                    isRead: false
                };
                
                await addNotification(sellerNotificationData);
                
                // Mark as processed
                await updateDoc(doc.ref, { notifications_sent: true });
                count++;
                
                console.log(`✅ Added notification for offer ${offerData.id}`);
            } catch (error) {
                console.error(`❌ Error adding notification for offer ${offerData.id}:`, error);
            }
        }
        
        console.log(`✅ Added notifications to ${count} offers`);
        return count;
    } catch (error) {
        console.error('❌ Error adding notifications to existing offers:', error);
        throw error;
    }
};

export const getSentOffersByUserId = async (userId) => {
    try {
        // Reference to the "offers" collection
        const offersRef = collection(db, "offers")

        const offers = []

        // Create a query to filter offers by sellerId
        const q = query(offersRef, where("sender", "==", userId))
        const querySnapshot = await getDocs(q)

        querySnapshot.forEach((doc) => {
            offers.push({ id: doc.id, ...doc.data() })
        })
        offers.sort((a, b) => b.created_at - a.created_at);

        return offers // Return the array of offers
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getReceivedOffersByUserId = async (userId) => {
    try {
        // Reference to the "offers" collection
        const offersRef = collection(db, "offers")

        const offers = []

        // Create a query to filter offers by sellerId
        const q = query(offersRef, where("receiver", "==", userId))
        const querySnapshot = await getDocs(q)

        querySnapshot.forEach((doc) => {
            offers.push({ id: doc.id, ...doc.data() })
        })

        offers.sort((a, b) => b.created_at - a.created_at);

        return offers // Return the array of offers
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const updateOffer = async (updatedData) => {
    try {
        // Reference to the specific offer document
        const offerRef = doc(db, "offers", updatedData.id)

        // Update the document with new data
        await updateDoc(offerRef, updatedData)

        console.log("Document updated with ID: ", updatedData.id)

        // Send notifications based on the state change
        try {
            if (updatedData.state === 'accepted') {
                // Send notification to buyer that their offer was accepted
                const buyerNotificationData = {
                    type: 'offer_status',
                    action: 'accepted',
                    listingId: updatedData.cardId,
                    listingTitle: updatedData.title,
                    recipientId: updatedData.sender, // Notify the buyer
                    message: `Your offer of $${updatedData.offer_price.toFixed(2)} for "${updatedData.title}" has been accepted!`,
                    timestamp: new Date(),
                    isRead: false
                };
                
                await addNotification(buyerNotificationData);
                console.log('✅ Offer accepted notification sent to buyer:', updatedData.sender);
            } else if (updatedData.state === 'declined') {
                // Send notification to buyer that their offer was declined
                const buyerNotificationData = {
                    type: 'offer_status',
                    action: 'declined',
                    listingId: updatedData.cardId,
                    listingTitle: updatedData.title,
                    recipientId: updatedData.sender, // Notify the buyer
                    message: `Your offer of $${updatedData.offer_price.toFixed(2)} for "${updatedData.title}" has been declined.`,
                    timestamp: new Date(),
                    isRead: false
                };
                
                await addNotification(buyerNotificationData);
                console.log('✅ Offer declined notification sent to buyer:', updatedData.sender);
            }
        } catch (notificationError) {
            console.error('❌ Error sending offer status notification:', notificationError);
        }
    } catch (e) {
        console.error("Error updating document: ", e)
        throw e;
    }
}
